import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import { OrderDocument, OrderItemDocument } from '@models/order';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import RadioButton from '@ui/RadioButton';
import SafeImage from '@ui/SafeImage';
import { getItem, logEvent } from '@util/analytics';
import { getChatsByOrderId } from '@util/firestore/messages';
import {
  ALL_REASONS,
  addNewNote,
  reasonSchema,
  reasons,
  refundOrder,
} from '@util/firestore/orders';
import { chargeAccount } from '@util/firestore/payments';
import { getProductsByIds } from '@util/firestore/products';
import { getUsersByIds } from '@util/firestore/users';
import { capitalizeFirstLetter, formatCurrency } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import BaseModal from './BaseModal';

const refundSchema = z.object({
  reason: reasonSchema,
  type: z.union([z.literal('full'), z.literal('partial')]),
  amount: z.number().optional(),
  item_ids: z.array(z.string()),
});

type Form = z.infer<typeof refundSchema>;

const RefundBuyerModal = ({
  open,
  dismiss,
  items,
  order,
}: {
  open: boolean;
  dismiss: () => void;
  items: OrderItemDocument[];
  order: OrderDocument;
}) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
    watch,
    setError,
    handleSubmit,
  } = useForm<Form>({
    mode: 'onSubmit',
    resolver: zodResolver(refundSchema),
    defaultValues: {
      reason: 'duplicate',
      type: 'full',
      item_ids: items.map((item) => item.id),
    },
  });

  const [isRefunding, setIsRefunding] = useState(false);

  const form = watch();
  const selectOptions = ALL_REASONS.map((reason) => ({
    id: reason,
    label: reason
      .split('_')
      .map((word) => capitalizeFirstLetter(word))
      .join(' '),
    value: reason,
  }));
  const { data: products } = useQuery({
    queryKey: ['products', ...items.map((item) => item.id)],
    queryFn: () => getProductsByIds(items.map((item) => item.id)),
  });
  const { userDoc } = useAuth();
  const { data: sellers } = useQuery({
    queryKey: ['sellers', ...items.map((item) => item.seller_id)],
    queryFn: () => getUsersByIds(items.map((item) => item.seller_id)),
    enabled: userDoc?.roles?.admin,
  });
  const { data: chats } = useQuery({
    queryKey: ['chats', order.id],
    queryFn: () => getChatsByOrderId(order.id),
  });
  const cancellationCosts: Record<string, { fee: number; min: number }> = {};
  sellers?.forEach((seller) => {
    const isFirst = seller.metrics.num_refunded === 0;
    const fee = isFirst ? 0.05 : 0.1;
    const min = isFirst ? 5 : 10;
    cancellationCosts[seller.uid] = {
      fee,
      min,
    };
  });
  const [chargeSeller, setChargeSeller] = useState<Map<string, number>>(
    new Map()
  );
  const queryClient = useQueryClient();

  const { showErrorToast, showSuccessToast } = useToastContext();

  const onSubmit = async () => {
    if (!chats) {
      showErrorToast('Error fetching chats');
      return;
    }
    if (
      form.type === 'partial' &&
      form.amount &&
      form.amount >
        items.reduce((acc, item) => acc + item.product_cost * item.qty, 0)
    ) {
      setError('amount', {
        type: 'manual',
        message: 'Amount cannot be greater than the product cost',
      });
      return;
    }

    setIsRefunding(true);

    try {
      // Refund Order
      await refundOrder({
        reason: form.reason,
        ...(form.type === 'partial' && { partial_amount: form.amount }),
        product_ids: form.item_ids,
        order: order,
        amount_charged: 0,
      });
      showSuccessToast('Order Refunded');

      // Charge Sellers
      if (chargeSeller.size > 0) {
        try {
          await Promise.all(
            chargeSeller.entries().map(async ([item_id, amount]) => {
              const item = items.find((item) => item.id === item_id);
              if (item) {
                const seller = sellers?.find(
                  (seller) => seller.uid === item.seller_id
                );
                await chargeAccount({
                  amount,
                  account_id: item.account_id,
                  description: `Order #${order.order_num} - Cancellation Fee`, // same as cancel order modal
                  order_id: order.id,
                  order_num: order.order_num,
                });
                await addNewNote(
                  order.id,
                  `Seller ${
                    seller?.username ?? item.seller_id
                  } charged $${amount} for refund fee for item ${
                    item.product_name
                  } due to ${form.reason}`
                );
              }
            })
          );
          showSuccessToast('Seller fees charged');
        } catch (e) {
          showErrorToast((e as Error).message);
        }
      }
      if (products) {
        logEvent('refund', {
          ...(order.coupon?.id && { coupon: order.coupon.id }),
          currency: 'USD',
          items: products.map(getItem),
          transaction_id: order.id,
          shipping: items.reduce(
            (acc, item) => acc + (item.buyer_shipping_cost ?? 0),
            0
          ),
          tax: items.reduce((acc, item) => acc + (item.product_tax ?? 0), 0),
          value: items.reduce(
            (acc, item) => acc + item.product_cost * item.qty,
            0
          ),
        });
      }
    } catch (e) {
      showErrorToast((e as Error).message);
    } finally {
      setIsRefunding(false);
      queryClient.invalidateQueries({
        queryKey: ['order', order.id],
      });
      dismiss();
    }
  };

  const formatRefund = (item: OrderItemDocument) => {
    const total = item.product_cost * item.qty + item.buyer_shipping_cost;
    if (form.amount) {
      return `${formatCurrency(total)} - ${formatCurrency(
        form.amount ?? 0
      )} = ${formatCurrency(total - (form.amount ?? 0))}`;
    } else {
      return `${formatCurrency(total)}`;
    }
  };

  const getTotalRefund = () => {
    return items.reduce(
      (acc, item) =>
        form.item_ids.includes(item.id)
          ? acc + item.product_cost * item.qty + item.buyer_shipping_cost
          : acc,
      0
    );
  };
  return (
    <BaseModal
      isOpen={open}
      dismiss={dismiss}
      title={'Refund Buyer'}
      overflowType="visible"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-w-[36rem] flex-col gap-[1.6rem] p-8"
      >
        <span className="text-gray-500">
          Check the items you wish to be part of the refund
        </span>
        {products?.map((product) => {
          const item = items.find((item) => item.id === product.id);
          if (!item) return null;
          return (
            <div className="flex items-center gap-[1.6rem]" key={product.id}>
              <input
                className="h-[1.8rem] w-[1.8rem] cursor-pointer"
                type="checkbox"
                checked={form.item_ids.includes(item.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('item_ids', [...form.item_ids, item.id]);
                  } else {
                    setValue(
                      'item_ids',
                      form.item_ids.filter((id) => id !== item.id)
                    );
                  }
                }}
              />

              <div className="relative h-[4.4rem] w-[4.4rem] rounded-2xl object-contain">
                <SafeImage
                  fill
                  alt={'Product image for ' + product.title}
                  src={product.images[0]?.full}
                  key={product.images[0]?.full}
                />
              </div>
              <div className="flex flex-col items-start ">
                <span>{product.title}</span>
                <span className="text-[1.3rem]">Qty: {item.qty}</span>
                <span className="text-[1.3rem]">
                  Shipping Cost: {formatCurrency(item.buyer_shipping_cost)}
                </span>
                <span className="text-[1.3rem]">
                  Cost: {formatCurrency(item.product_cost)}
                </span>
                {form.type === 'full' && (
                  <span className="text-[1.3rem]">
                    Refund:{' '}
                    {form.item_ids.includes(item.id)
                      ? formatRefund(item)
                      : formatCurrency(0)}
                  </span>
                )}
              </div>
              <label>
                Charge seller for this item (
                {formatCurrency(
                  Math.max(
                    cancellationCosts[item.seller_id]?.fee *
                      item.product_cost *
                      item.qty,
                    cancellationCosts[item.seller_id]?.min
                  )
                )}
                )
              </label>
              <input
                className="h-[1.8rem] w-[1.8rem] cursor-pointer"
                type="checkbox"
                checked={!!chargeSeller.get(item.id)}
                onChange={() => {
                  if (chargeSeller.get(item.id)) {
                    chargeSeller.delete(item.id);
                    setChargeSeller(new Map(chargeSeller));
                  } else {
                    const seller = sellers?.find(
                      (seller) => seller.uid === item.seller_id
                    );
                    if (seller) {
                      const isFirst = seller.metrics.num_refunded === 0;
                      const fee = isFirst ? 0.05 : 0.1;
                      const min = isFirst ? 5 : 10;
                      chargeSeller.set(
                        item.id,
                        Math.max(fee * item.product_cost * item.qty, min)
                      );
                      setChargeSeller(new Map(chargeSeller));
                    } else {
                      alert('Seller not found for this item');
                    }
                  }
                }}
                id="charge-seller"
              />
            </div>
          );
        })}
        {form.reason === 'requested_by_customer' ? (
          <div className="text-[1.2rem] text-red-600">
            Buyer will be charged a 3.5% processing fee. <br /> This will come
            out of the refund amount
          </div>
        ) : form.reason === 'requested_by_seller' ? (
          <div className="text-[1.2rem] text-red-600">
            Please charge a 11% seller cancellation fee to the affected sellers
            after refunding. <br />
          </div>
        ) : null}
        <FormLabel value="Reason" required>
          <FormSelect
            control={control}
            allowEmpty={false}
            name="reason"
            defaultValue={reasons[0].value}
            options={selectOptions}
            onChange={(e) => {
              setValue('reason', e.value);
            }}
          ></FormSelect>
        </FormLabel>
        <FormLabel value="Select Amount" required>
          <div className="flex w-full justify-evenly">
            <RadioButton
              checked={form.type === 'full'}
              label="Full Refund"
              onClick={() => {
                setValue('type', 'full');
                setValue('amount', 0);
              }}
            />
            <RadioButton
              checked={form.type === 'partial'}
              label="Partial Refund"
              onClick={() => {
                setValue('type', 'partial');
                setValue('amount', 0);
              }}
            />
          </div>
        </FormLabel>
        {form.type === 'partial' && (
          <>
            <FormLabel
              value="Amount"
              required
              errorMessage={errors.amount?.message}
            >
              <FormInput
                {...register('amount', {
                  valueAsNumber: true,
                })}
                type="number"
                error={!!errors.amount}
              />
            </FormLabel>
          </>
        )}
        {form.reason === 'requested_by_customer' && (
          <span>
            The buyer will receive{' '}
            {form.amount
              ? (form.amount * 0.965).toFixed(2)
              : (getTotalRefund() * 0.965).toFixed(2)}
          </span>
        )}
        <Button
          buttonType="submit"
          text="Submit Refund"
          type="secondary"
          loading={isRefunding}
        />
      </form>
    </BaseModal>
  );
};

export default RefundBuyerModal;
